import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useToast } from '../context/ToastContext';
import ProductCard from '../components/ProductCard';
import ProductGallery from '../components/ProductGallery';
import ProductReviews from '../components/ProductReviews';
import SizeSelector from '../components/SizeSelector';
import { StoreError, StoreLoading } from '../components/StoreState';
import { useLocale } from '../context/LocaleContext';
import { useBrands } from '../hooks/useBrands';
import { useProducts } from '../hooks/useProducts';
import { fetchProductById, getImagesForVariant } from '../lib/products';
import { rememberProductSportSlug } from '../lib/productSportCache';
import { summarizeReviews } from '../lib/reviews';
import { isSupabaseConfigured } from '../lib/supabaseClient';
import {
  getSportByName,
  getSportBySlug,
  productBelongsToSport,
  productBelongsToTeam,
  getProductTeam,
  sportToSlug,
} from '../utils/sports';
import { resolveProductSeoTitle } from '../utils/seo';
import { getProductBrandName } from '../utils/brands';
import './Product.css';

function ChevronDown() {
  return (
    <svg width="14" height="8" viewBox="0 0 14 8" fill="none" aria-hidden="true">
      <path
        d="M1 1.25L7 6.75L13 1.25"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Stars({ average, count }) {
  const filled = Math.round(average || 0);

  return (
    <div className="product-rating" aria-label={`${average || 0} out of 5 stars, ${count} reviews`}>
      <span className="product-rating-stars" aria-hidden="true">
        {[1, 2, 3, 4, 5].map(star => (
          <span key={star} className={star <= filled ? 'filled' : 'empty'}>
            ★
          </span>
        ))}
      </span>
      <span className="product-rating-count">({count})</span>
    </div>
  );
}

function Product() {
  const { productId } = useParams();
  const { addToCart, cartItems } = useCart();
  const { pushToast } = useToast();
  const { formatMoney } = useLocale();
  const { products: catalogue } = useProducts();
  const { brands } = useBrands();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedVariantId, setSelectedVariantId] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [sizeError, setSizeError] = useState('');
  const [added, setAdded] = useState(false);
  const [openAccordion, setOpenAccordion] = useState('');
  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const [reviewStats, setReviewStats] = useState({ average: 0, count: 0 });

  useEffect(() => {
    if (!isSupabaseConfigured) {
      setError('This product is temporarily unavailable.');
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    async function loadProduct() {
      setLoading(true);
      setError('');

      try {
        const data = await fetchProductById(productId);

        if (cancelled) {
          return;
        }

        setProduct(data);
        rememberProductSportSlug(productId, data);
        setSelectedSize('');
        setSelectedVariantId(data.defaultVariantId || data.variants?.[0]?.id || '');
        setQuantity(1);
      } catch {
        if (!cancelled) {
          setError('Product not found or could not be loaded.');
          setProduct(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadProduct();

    return () => {
      cancelled = true;
    };
  }, [productId]);

  useEffect(() => {
    if (!product) return undefined;

    const previousTitle = document.title;
    document.title = resolveProductSeoTitle(product);

    const meta = document.querySelector('meta[name="description"]');
    const previousDescription = meta?.getAttribute('content') || '';
    const fromMeta = String(product.meta_description || '').trim();
    const fromDescription = String(product.description || '')
      .trim()
      .replace(/\s+/g, ' ')
      .slice(0, 155);
    const nextDescription = fromMeta || fromDescription || previousDescription;

    if (meta && nextDescription) {
      meta.setAttribute('content', nextDescription);
    }

    return () => {
      document.title = previousTitle;
      if (meta) {
        meta.setAttribute('content', previousDescription);
      }
    };
  }, [product]);

  const galleryImages = useMemo(() => {
    if (!product) return [];
    return getImagesForVariant(product.images, selectedVariantId || null);
  }, [product, selectedVariantId]);

  const selectedSizeStock = useMemo(() => {
    if (!product?.hasSizeVariants || !selectedSize) return null;
    const size = (product.sizes || []).find(item => item.size_label === selectedSize);
    if (!size || size.enabled === false) return 0;
    return Math.max(0, Number(size.stock_quantity) || 0);
  }, [product, selectedSize]);

  const quantityAlreadyInCart = useMemo(() => {
    if (!product?.id) return 0;
    return cartItems
      .filter(item => item.id === product.id && (item.size || null) === (selectedSize || null))
      .reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
  }, [cartItems, product, selectedSize]);

  const maxOrderableQty = useMemo(() => {
    if (selectedSizeStock == null) return 10;
    return Math.max(0, selectedSizeStock - quantityAlreadyInCart);
  }, [selectedSizeStock, quantityAlreadyInCart]);

  const quantityOptions = useMemo(() => {
    const max = Math.max(1, maxOrderableQty);
    return Array.from({ length: max }, (_, index) => index + 1);
  }, [maxOrderableQty]);

  useEffect(() => {
    if (maxOrderableQty < 1) {
      setQuantity(1);
      return;
    }
    setQuantity(current => Math.min(current, maxOrderableQty));
  }, [maxOrderableQty, selectedSize]);

  const displayPrice = product?.sale_price != null ? product.sale_price : product?.price;

  const colourOptions = useMemo(() => {
    if (!product?.variants?.length) return [];

    return product.variants.map(variant => ({
      id: variant.id,
      name: variant.name,
      color: variant.color_hex || product.color || '#111',
    }));
  }, [product]);

  const detailsLines = useMemo(() => {
    if (!product) return [];

    const sportLabel =
      getSportByName(product.sport || product.category)?.name ||
      product.sport ||
      product.category ||
      '';
    const categoryLabel =
      getSportByName(product.category)?.name || product.category || '';
    const showCategory =
      categoryLabel &&
      categoryLabel.toLowerCase() !== String(sportLabel).toLowerCase();

    const brandName = getProductBrandName(product, brands);
    const teamName = (getProductTeam(product) || '').trim();
    const showBrand =
      brandName && brandName.toLowerCase() !== teamName.toLowerCase();

    return [
      showBrand && `Brand: ${brandName}`,
      teamName && `Team: ${teamName}`,
      product.league && `League: ${product.league}`,
      sportLabel && `Sport: ${sportLabel}`,
      showCategory && `Category: ${categoryLabel}`,
      product.sku && `SKU: ${product.sku}`,
    ].filter(Boolean);
  }, [product, brands]);

  const relatedProducts = useMemo(() => {
    if (!product || !catalogue?.length) return [];
    const sport =
      getSportBySlug(product.sport) ||
      getSportByName(product.sport) ||
      getSportByName(product.category) ||
      getSportBySlug(sportToSlug(product.category || ''));
    const team = getProductTeam(product);

    const sameTeam = team
      ? catalogue.filter(
          item =>
            item.id !== product.id && productBelongsToTeam(item, team, sport)
        )
      : [];
    const sameSport =
      sport
        ? catalogue.filter(
            item =>
              item.id !== product.id &&
              !sameTeam.some(entry => entry.id === item.id) &&
              productBelongsToSport(item, sport)
          )
        : [];

    return [...sameTeam, ...sameSport].slice(0, 4);
  }, [catalogue, product]);

  const buildCartProduct = () => {
    const primaryImage = galleryImages[0]?.image_url || product.image_url || null;
    const variant = (product.variants || []).find(v => v.id === selectedVariantId);

    return {
      id: product.id,
      name: product.name,
      category: product.category,
      price: displayPrice,
      color: variant?.color_hex || product.color,
      image_url: primaryImage,
      size: selectedSize || null,
      variant: variant?.name || null,
      availableStock: selectedSizeStock,
    };
  };

  const validateSize = () => {
    if (product.hasSizeVariants && !selectedSize) {
      setSizeError('Please select a size.');
      return false;
    }

    if (product.hasSizeVariants && maxOrderableQty < 1) {
      setSizeError(
        quantityAlreadyInCart > 0
          ? `You already have all ${selectedSizeStock} available in your bag.`
          : 'This size is out of stock.'
      );
      return false;
    }

    setSizeError('');
    return true;
  };

  const handleAddToCart = () => {
    if (!validateSize()) {
      return;
    }

    const qty = Math.min(quantity, Math.max(1, maxOrderableQty));
    addToCart(buildCartProduct(), qty);
    setAdded(true);
    pushToast(
      qty === 1
        ? `Added ${product.name}${selectedSize ? ` · ${selectedSize}` : ''} to bag`
        : `Added ${qty} × ${product.name}${selectedSize ? ` · ${selectedSize}` : ''} to bag`
    );
    setTimeout(() => setAdded(false), 1500);
  };

  if (loading) {
    return (
      <main className="product-page">
        <div className="product-status">
          <StoreLoading label="Loading product…" />
        </div>
      </main>
    );
  }

  if (error || !product) {
    return (
      <main className="product-page">
        <div className="product-status">
          <StoreError message={error || 'Product not found.'} />
          <Link to="/shop" className="store-link" style={{ marginTop: 24, display: 'inline-flex' }}>
            Back to shop
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="product-page">
      <div className="product-layout">
        <section className="product-gallery-panel" aria-label="Product images">
          <ProductGallery
            images={galleryImages}
            productName={product.name}
          />
        </section>

        <section className="product-details">
          <h1 className="product-title">{product.name}</h1>

          <div className="product-price-rating">
            <div className="product-price">
              {formatMoney(displayPrice)}
              {product.sale_price != null && (
                <span className="product-price-was">{formatMoney(product.price)}</span>
              )}
            </div>
            <Stars average={reviewStats.average} count={reviewStats.count} />
          </div>

          {colourOptions.length > 1 && (
            <div className="product-colours">
              <span className="product-colours-label">Colours:</span>
              <div className="product-swatches" role="listbox" aria-label="Select colour">
                {colourOptions.map(option => {
                  const isActive = selectedVariantId === option.id;

                  return (
                    <button
                      key={option.id}
                      type="button"
                      role="option"
                      aria-selected={isActive}
                      aria-label={option.name}
                      title={option.name}
                      className={`product-swatch${isActive ? ' active' : ''}`}
                      onClick={() => setSelectedVariantId(option.id)}
                    >
                      <span
                        className="product-swatch-fill"
                        style={{ background: option.color }}
                      />
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {product.hasSizeVariants && (
            <div className="product-size-block">
              <div className="product-size-guide-row">
                <span className="product-size-label">Size</span>
                <button
                  type="button"
                  className="product-size-guide"
                  onClick={() => setShowSizeGuide(true)}
                >
                  Size guide
                </button>
              </div>
              <SizeSelector
                sizes={product.sizes}
                selectedSize={selectedSize}
                onSelectSize={label => {
                  setSelectedSize(label);
                  setSizeError('');
                }}
              />
            </div>
          )}

          <div className="product-qty-row">
            <label className="product-qty-label" htmlFor="product-qty">
              Quantity
            </label>
            <select
              id="product-qty"
              className="product-select"
              value={Math.min(quantity, quantityOptions[quantityOptions.length - 1] || 1)}
              onChange={e => setQuantity(Number(e.target.value))}
              aria-label="Quantity"
              disabled={
                product.isOutOfStock ||
                (product.hasSizeVariants && (!selectedSize || maxOrderableQty < 1))
              }
            >
              {quantityOptions.map(value => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
            {product.hasSizeVariants && selectedSize && selectedSizeStock != null && (
              <span className="product-qty-stock">
                {maxOrderableQty < 1
                  ? 'None left for this size'
                  : `${selectedSizeStock} available`}
              </span>
            )}
          </div>

          {product.isOutOfStock && (
            <p className="product-hint">Currently unavailable.</p>
          )}
          {sizeError && <p className="product-hint">{sizeError}</p>}

          <button
            type="button"
            className={`product-add-btn${added ? ' added' : ''}`}
            onClick={handleAddToCart}
            disabled={
              product.isOutOfStock ||
              (product.hasSizeVariants && selectedSize && maxOrderableQty < 1)
            }
          >
            {added ? 'Added' : 'Add to bag'}
          </button>

          <p className="product-trust">
            Licensed goods · Secure checkout ·{' '}
            <Link to="/shipping">Shipping policy</Link>
          </p>

          <div className="product-accordions">
            <div className="product-accordion">
              <button
                type="button"
                className="product-accordion-trigger"
                aria-expanded={openAccordion === 'description'}
                onClick={() =>
                  setOpenAccordion(current =>
                    current === 'description' ? '' : 'description'
                  )
                }
              >
                Description
                <span className="product-accordion-chevron">
                  <ChevronDown />
                </span>
              </button>
              {openAccordion === 'description' && (
                <div className="product-accordion-panel">
                  <p>{product.description || 'No description available.'}</p>
                </div>
              )}
            </div>

            <div className="product-accordion">
              <button
                type="button"
                className="product-accordion-trigger"
                aria-expanded={openAccordion === 'details'}
                onClick={() =>
                  setOpenAccordion(current => (current === 'details' ? '' : 'details'))
                }
              >
                Details
                <span className="product-accordion-chevron">
                  <ChevronDown />
                </span>
              </button>
              {openAccordion === 'details' && (
                <div className="product-accordion-panel">
                  {detailsLines.length > 0 ? (
                    detailsLines.map(line => <p key={line}>{line}</p>)
                  ) : (
                    <p>No additional details available.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>
      </div>

      {relatedProducts.length > 0 && (
        <section className="product-related" aria-label="Related kit">
          <p className="store-eyebrow">More from the house</p>
          <h2 className="product-related-title">Related kit</h2>
          <div className="product-card-grid">
            {relatedProducts.map(item => (
              <ProductCard key={item.id} product={item} />
            ))}
          </div>
        </section>
      )}

      <ProductReviews
        productId={product.id}
        onStatsChange={reviews => setReviewStats(summarizeReviews(reviews))}
      />

      {showSizeGuide && (
        <div
          className="product-modal-backdrop"
          role="presentation"
          onClick={() => setShowSizeGuide(false)}
        >
          <div
            className="product-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Size guide"
            onClick={event => event.stopPropagation()}
          >
            <h2>Size Guide</h2>
            <p>Use this chart as a general guide. Fit can vary by style.</p>
            <table>
              <thead>
                <tr>
                  <th>Size</th>
                  <th>Chest</th>
                  <th>Length</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>XS</td><td>34–36"</td><td>26"</td></tr>
                <tr><td>S</td><td>36–38"</td><td>27"</td></tr>
                <tr><td>M</td><td>38–40"</td><td>28"</td></tr>
                <tr><td>L</td><td>40–42"</td><td>29"</td></tr>
                <tr><td>XL</td><td>42–44"</td><td>30"</td></tr>
                <tr><td>XXL</td><td>44–46"</td><td>31"</td></tr>
              </tbody>
            </table>
            <button
              type="button"
              className="product-modal-close"
              onClick={() => setShowSizeGuide(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </main>
  );
}

export default Product;
